import { PrintDeliverySheetButton } from "@/components/orders/print-delivery-sheet-button";
// routes/_main/orders/index.tsx
import { OrdersStream } from "@/components/tables/orders-stream"; // NEW
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { buildMetadata } from "@/lib/metadata";
import { useAuth } from "@/providers/auth";
import { OrderService } from "@/services/order-services";
import type { Order } from "@/services/types/orders";
import { Link, createFileRoute } from "@tanstack/react-router";
import { UploadCloud } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_main/orders/")({
  component: RouteComponent,
  loader: async () => {
    // ✅ Only fetch the FIRST page so we can render immediately
    const firstPage = await OrderService.getOrdersPage({
      page: 1,
      pageSize: 10,
    });
    return { firstPage };
  },
  head: () => ({
    meta: buildMetadata({
      title: "Channel Genie",
      description: "View all orders",
      keywords: ["orders", "order", "view"],
    }),
  }),
});

function RouteComponent() {
  const { firstPage } = Route.useLoaderData() as {
    firstPage: Awaited<ReturnType<typeof OrderService.getOrdersPage>>;
  };
  const [loadedOrders, setLoadedOrders] = useState<Order[]>(
    () => firstPage?.results ?? [],
  );
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const { user } = useAuth();
  const isWarehouseUser = user?.supplierRole === "warehouse_user";
  const hasOrders = loadedOrders.length > 0;

  return (
    <div className="space-y-8 p-4 md:p-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1" />
        <div className="flex gap-3 self-start">
          <div className="flex items-center gap-2">
            {selectedOrderIds.length > 0 && (
              <Badge
                variant="secondary"
                className="rounded-full px-2.5 py-1 font-medium text-[10px] uppercase tracking-wide"
              >
                Selected: {selectedOrderIds.length}
              </Badge>
            )}
            <PrintDeliverySheetButton
              hasOrders={hasOrders}
              orders={loadedOrders}
              selectedOrderIds={selectedOrderIds}
            />
          </div>
          {!isWarehouseUser && (
            <Button
              asChild
              size="lg"
              className="rounded-xl bg-foreground text-background hover:bg-foreground/90"
            >
              <Link to="/orders/upload">
                <UploadCloud className="size-4" />
                Upload Orders
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* ✅ This component renders page 1 immediately and streams the rest */}
      <OrdersStream
        firstPage={firstPage}
        pageSize={50}
        onOrdersChange={setLoadedOrders}
        onSelectedOrderIdsChange={setSelectedOrderIds}
      />
    </div>
  );
}
